/**
 * /verify — Coupon Verifier tool.
 *
 * Standalone route for part-time event staff. Login-gated (verifier
 * role JWT), separate from vendor & admin auth. Single search box
 * (phone number OR coupon code), cross-vendor results, single-click
 * Redeem action. No linking from anywhere else in the app.
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/ui/password-input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import {
  Loader2,
  Search,
  Ticket,
  CheckCircle2,
  LogOut,
  ShieldCheck,
  Phone,
  Store,
  Calendar,
  Gift,
} from "lucide-react";

const TOKEN_KEY = "citydooh_verifier_token";
const VERIFIER_KEY = "citydooh_verifier_profile";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

// ============================================================
// Login screen — only rendered when no verifier token is present.
// ============================================================
const LoginScreen = ({ onSuccess }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      toast.error("Enter your email and password.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.verifierLogin({
        email: email.trim().toLowerCase(),
        password,
      });
      localStorage.setItem(TOKEN_KEY, r.data.token);
      localStorage.setItem(VERIFIER_KEY, JSON.stringify(r.data.verifier));
      toast.success(`Welcome, ${r.data.verifier.name || "verifier"}`);
      onSuccess(r.data.verifier);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Login failed. Check your credentials.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen grid place-items-center px-4 py-10 bg-background"
      data-testid="verifier-login-screen"
    >
      <Card className="w-full max-w-md p-8 space-y-6 shadow-xl">
        <div className="text-center space-y-2">
          <div className="mx-auto h-14 w-14 rounded-2xl bg-primary/10 grid place-items-center">
            <ShieldCheck
              className="h-7 w-7 text-primary"
              aria-hidden
            />
          </div>
          <h1 className="font-heading text-2xl font-semibold">
            Coupon Verifier
          </h1>
          <p className="text-sm text-muted-foreground">
            Sign in with your CityDooh verifier account to look up and
            redeem winning coupons on the ground.
          </p>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="v-email">Email</Label>
            <Input
              id="v-email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@citydooh.com"
              required
              data-testid="verifier-login-email"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="v-password">Password</Label>
            <PasswordInput
              id="v-password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              data-testid="verifier-login-password"
            />
          </div>
          <Button
            type="submit"
            className="w-full h-11"
            disabled={busy}
            data-testid="verifier-login-submit"
          >
            {busy ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <ShieldCheck className="h-4 w-4 mr-2" />
            )}
            Sign in
          </Button>
        </form>

        <p className="text-xs text-center text-muted-foreground">
          Restricted access. This tool only lets you look up and redeem
          winning coupons — nothing else.
        </p>
      </Card>
    </div>
  );
};

// ============================================================
// Result row — one match card. Shows enough context to confirm
// "this person won this prize at this store" and (if the coupon
// is still redeemable) a Redeem button.
// ============================================================
const ResultRow = ({ row, onRedeem, redeeming }) => {
  const { lead, coupon, campaign, vendor } = row;
  const isRedeemed = coupon?.is_redeemed;
  const hasCoupon = !!coupon;

  return (
    <Card
      className="p-5 space-y-4"
      data-testid={`verifier-result-${lead?.id || coupon?.id}`}
    >
      <div className="flex flex-wrap items-start gap-3 justify-between">
        <div className="space-y-1 min-w-0">
          <div className="font-heading text-lg font-semibold truncate">
            {lead?.name || "—"}
          </div>
          <div className="text-xs text-muted-foreground flex items-center gap-1.5">
            <Phone className="h-3.5 w-3.5" aria-hidden />
            <span className="font-mono">{lead?.phone || "—"}</span>
          </div>
        </div>
        {hasCoupon && (
          (() => {
            const untrackable = coupon.redemption_trackable === false;
            if (untrackable) {
              return (
                <Badge
                  variant="outline"
                  className="border-sky-500/40 text-sky-500"
                  data-testid="verifier-result-status"
                  title="External partner code — CityDooh does not track redemption."
                >
                  Delivered
                </Badge>
              );
            }
            return (
              <Badge
                variant={isRedeemed ? "secondary" : "default"}
                className={isRedeemed ? "" : "bg-emerald-600 hover:bg-emerald-600"}
                data-testid="verifier-result-status"
              >
                {isRedeemed ? "Redeemed" : "Ready to redeem"}
              </Badge>
            );
          })()
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3 text-sm">
        <div className="space-y-0.5">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">
            <Gift className="h-3.5 w-3.5 inline mr-1" aria-hidden />
            Prize won
          </div>
          <div className="font-medium">
            {lead?.won_gift_name || lead?.won_label || coupon?.gift_name || "—"}
          </div>
        </div>
        <div className="space-y-0.5">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">
            <Ticket className="h-3.5 w-3.5 inline mr-1" aria-hidden />
            Coupon code
          </div>
          <div className="font-mono text-sm">{coupon?.code || "—"}</div>
        </div>
        <div className="space-y-0.5">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">
            <Store className="h-3.5 w-3.5 inline mr-1" aria-hidden />
            Store / campaign
          </div>
          <div>
            {vendor?.business || "—"}
            {campaign?.name ? (
              <span className="text-muted-foreground"> · {campaign.name}</span>
            ) : null}
            {vendor?.city ? (
              <span className="text-muted-foreground"> · {vendor.city}</span>
            ) : null}
          </div>
        </div>
        <div className="space-y-0.5">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">
            <Calendar className="h-3.5 w-3.5 inline mr-1" aria-hidden />
            Spun
          </div>
          <div>{fmtDate(lead?.spun_at)}</div>
        </div>
        {isRedeemed && (
          <div className="space-y-0.5 sm:col-span-2">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">
              Redeemed at
            </div>
            <div>{fmtDate(coupon?.redeemed_at)}</div>
          </div>
        )}
      </div>

      {hasCoupon && !isRedeemed && coupon.redemption_trackable !== false && (
        <div className="pt-2 border-t border-border/60">
          <Button
            onClick={() => onRedeem(coupon.id)}
            disabled={redeeming === coupon.id}
            className="w-full sm:w-auto"
            data-testid={`verifier-redeem-btn-${coupon.id}`}
          >
            {redeeming === coupon.id ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <CheckCircle2 className="h-4 w-4 mr-2" />
            )}
            Mark as redeemed
          </Button>
        </div>
      )}
      {hasCoupon && coupon.redemption_trackable === false && (
        <div className="pt-2 border-t border-border/60">
          <p className="text-xs text-muted-foreground">
            This is an external partner code — CityDooh doesn&apos;t track its redemption. The customer redeems it directly with the partner.
          </p>
        </div>
      )}
    </Card>
  );
};

// ============================================================
// VendorSearchForm — matched-pair search for vendors only.
// Requires BOTH phone + coupon code together. Search button
// stays disabled until both fields have content.
// ============================================================
const VendorSearchForm = ({ onSubmit, busy }) => {
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const canSubmit = phone.trim().length > 0 && code.trim().length > 0 && !busy;

  return (
    <Card className="p-5 space-y-3">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!canSubmit) return;
          onSubmit({ phone: phone.trim(), code: code.trim() });
        }}
        className="space-y-3"
        data-testid="verifier-vendor-form"
      >
        <p className="text-xs text-muted-foreground">
          Enter the customer&apos;s phone number and the coupon code they&apos;re showing you.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="v-phone">Phone number</Label>
            <Input
              id="v-phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="e.g. 9812345678"
              inputMode="tel"
              autoComplete="off"
              className="h-11 text-base"
              data-testid="verifier-vendor-phone"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="v-code">Coupon code</Label>
            <Input
              id="v-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="e.g. CTD-ABC123"
              autoComplete="off"
              className="h-11 text-base font-mono"
              data-testid="verifier-vendor-code"
            />
          </div>
        </div>
        <div className="flex justify-end">
          <Button
            type="submit"
            disabled={!canSubmit}
            className="h-11 px-6"
            data-testid="verifier-vendor-submit"
          >
            {busy ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Search className="h-4 w-4 mr-2" />
            )}
            Search
          </Button>
        </div>
      </form>
    </Card>
  );
};

// ============================================================
// Main search + results screen.
// ============================================================
const VerifierApp = ({ verifier, onSignOut }) => {
  const isVendor = (verifier?.role || "").toLowerCase() === "vendor";
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [redeeming, setRedeeming] = useState(null);
  const [results, setResults] = useState([]);
  const [kind, setKind] = useState(null);
  const [searched, setSearched] = useState(false);
  // Disambiguation flow — admin/verifier only. Never triggered on the
  // vendor form, since vendor searches ALREADY carry a phone number.
  const [sharedCodeQuery, setSharedCodeQuery] = useState("");
  const [sharedPhone, setSharedPhone] = useState("");

  const runSearch = async (e, opts = {}) => {
    if (e) e.preventDefault();
    // Two entry points now:
    //   * admin/verifier — {q} single field
    //   * vendor         — {phone, code} matched pair
    // We branch here so upstream forms don't need to know the API
    // shape.
    let params = null;
    if (isVendor && (opts.phone || opts.code)) {
      const phoneRaw = (opts.phone || "").trim();
      const codeRaw  = (opts.code  || "").trim();
      if (!phoneRaw || !codeRaw) {
        toast.error("Enter both the phone number and the coupon code.");
        return;
      }
      params = { phone: phoneRaw, code: codeRaw };
    } else {
      const q = opts.q ?? query.trim();
      const phone = opts.phone ?? "";
      if (q.length < 3) {
        toast.error("Enter at least 3 characters — phone or coupon code.");
        return;
      }
      params = { q, phone };
    }
    setBusy(true);
    setSearched(true);
    try {
      const r = await api.verifierSearch(params);
      setResults(Array.isArray(r.data?.results) ? r.data.results : []);
      setKind(r.data?.kind || null);
      setSharedCodeQuery("");
      setSharedPhone("");
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      const errCode = typeof detail === "object" ? detail?.code : null;
      if (isVendor && status === 400 && errCode === "both_fields_required") {
        // Client-side gate already prevents this, but if the API
        // returns it (e.g. a URL-crafted request), show it plainly.
        toast.error(detail.message);
      } else if (status === 409 && errCode === "coupon_needs_phone") {
        setSharedCodeQuery(params.q || "");
        setResults([]);
        setKind(null);
        toast.info(
          typeof detail === "object"
            ? detail.message
            : "This code is shared — enter the winner's phone number to narrow down.",
        );
      } else if (status === 404) {
        setResults([]);
        setKind(null);
        // Vendor UX: no hint at which field was wrong — spec calls
        // out that this could help brute-forcing.
        toast.error(
          isVendor
            ? "No matching record found."
            : (typeof detail === "object" ? detail?.message : "No matching lead or coupon found."),
        );
      } else if (status === 401 || status === 403) {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(VERIFIER_KEY);
        onSignOut();
      } else {
        toast.error(
          typeof detail === "string" ? detail : "Search failed. Try again.",
        );
      }
    } finally {
      setBusy(false);
    }
  };

  const doRedeem = async (couponId) => {
    setRedeeming(couponId);
    try {
      await api.verifierRedeem(couponId);
      toast.success("Coupon marked as redeemed.");
      // Merge the redeemed state into the visible row without a full
      // re-fetch — snappier UX on-site.
      const now = new Date().toISOString();
      setResults((rows) =>
        rows.map((r) =>
          r.coupon?.id === couponId
            ? {
                ...r,
                coupon: {
                  ...r.coupon,
                  is_redeemed: true,
                  redeemed_at: now,
                },
              }
            : r,
        ),
      );
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Redemption failed. Try again.",
      );
    } finally {
      setRedeeming(null);
    }
  };

  return (
    <div
      className="min-h-screen bg-background"
      data-testid="verifier-app"
    >
      <header className="border-b border-border bg-card">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-10 w-10 rounded-xl bg-primary/10 grid place-items-center shrink-0">
              <ShieldCheck className="h-5 w-5 text-primary" aria-hidden />
            </div>
            <div className="min-w-0">
              <div className="font-heading text-lg font-semibold leading-tight">
                Coupon Verifier
              </div>
              <div
                className="text-xs text-muted-foreground truncate"
                data-testid="verifier-header-email"
              >
                {verifier?.name || "verifier"} · {verifier?.email}
              </div>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={onSignOut}
            data-testid="verifier-signout-btn"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Sign out
          </Button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8 space-y-6">
        {isVendor ? (
          <VendorSearchForm
            busy={busy}
            onSubmit={({ phone, code }) => runSearch(null, { phone, code })}
          />
        ) : (
          <Card className="p-5 space-y-3">
            <form onSubmit={runSearch} className="flex gap-2 items-stretch">
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Enter phone number or coupon code"
                className="flex-1 h-11 text-base"
                data-testid="verifier-search-input"
                autoFocus
              />
              <Button
                type="submit"
                disabled={busy}
                className="h-11 px-5"
                data-testid="verifier-search-submit"
              >
                {busy ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Search className="h-4 w-4 mr-2" />
                )}
                Search
              </Button>
            </form>
            <p className="text-xs text-muted-foreground">
              Try a coupon code like <span className="font-mono">CTD-ABC123</span> or a phone number.
              Cross-vendor search — you&apos;ll see winners from every store on the platform.
            </p>
          </Card>
        )}

        {sharedCodeQuery && !isVendor && (
          <Card
            className="p-5 space-y-3 border-amber-500/40 bg-amber-500/5"
            data-testid="verifier-disambiguation-card"
          >
            <div className="text-sm font-medium">
              <span className="font-mono">{sharedCodeQuery}</span> is a shared partner code.
            </div>
            <p className="text-xs text-muted-foreground">
              Enter the winner&apos;s phone number so we can find their specific record.
            </p>
            <form
              className="flex gap-2 items-stretch"
              onSubmit={(e) => {
                e.preventDefault();
                if (!sharedPhone.trim()) return;
                runSearch(null, { q: sharedCodeQuery, phone: sharedPhone.trim() });
              }}
            >
              <Input
                value={sharedPhone}
                onChange={(e) => setSharedPhone(e.target.value)}
                placeholder="Winner's phone number"
                className="flex-1 h-10"
                data-testid="verifier-disambiguation-phone"
                autoFocus
              />
              <Button
                type="submit"
                disabled={busy || !sharedPhone.trim()}
                data-testid="verifier-disambiguation-submit"
              >
                {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Phone className="h-4 w-4 mr-1" />}
                Look up
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setSharedCodeQuery("");
                  setSharedPhone("");
                }}
                data-testid="verifier-disambiguation-cancel"
              >
                Cancel
              </Button>
            </form>
          </Card>
        )}

        {searched && !busy && !sharedCodeQuery && results.length === 0 && (
          <Card
            className="p-8 text-center space-y-2"
            data-testid="verifier-empty-state"
          >
            <div className="mx-auto h-12 w-12 rounded-full bg-muted grid place-items-center">
              <Search className="h-5 w-5 text-muted-foreground" aria-hidden />
            </div>
            <div className="font-medium">
              {isVendor ? "No matching record found" : "No matches"}
            </div>
            <p className="text-sm text-muted-foreground">
              {isVendor
                ? "Double-check the phone number and coupon code with the customer, then try again."
                : "We couldn't find any lead or coupon for that phone number or code. Double-check and try again."}
            </p>
          </Card>
        )}

        {results.length > 0 && (
          <div className="space-y-3" data-testid="verifier-results-list">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">
              {results.length} {results.length === 1 ? "result" : "results"}
              {kind === "coupon" ? " · coupon match" : kind === "phone" ? " · phone match" : ""}
            </div>
            {results.map((row, i) => (
              <ResultRow
                key={row.lead?.id || row.coupon?.id || i}
                row={row}
                onRedeem={doRedeem}
                redeeming={redeeming}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

// ============================================================
// Root page component — hydrates from localStorage, verifies the
// stored token against /verify/me before showing the app.
// ============================================================
export default function VerifierPage() {
  const [verifier, setVerifier] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setLoading(false);
      return () => {
        alive = false;
      };
    }
    api
      .verifierMe()
      .then((r) => alive && setVerifier(r.data))
      .catch(() => {
        // Token invalid / account suspended — clear and force re-login.
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(VERIFIER_KEY);
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  const handleSignOut = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(VERIFIER_KEY);
    setVerifier(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <>
      <Toaster richColors position="top-center" />
      {verifier ? (
        <VerifierApp verifier={verifier} onSignOut={handleSignOut} />
      ) : (
        <LoginScreen onSuccess={setVerifier} />
      )}
    </>
  );
}
